import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../context/JourneyContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, BookOpen, ShoppingBag, ArrowRight, School, Users, Building2, Briefcase, Handshake, GraduationCap } from 'lucide-react';

// Official Let's Siva logo
const WHITE_LOGO = "https://customer-assets.emergentagent.com/job_dance-confidence/artifacts/00kbs5d6_Lets%20Siva_Final%20Logo_Black.png";

const PROGRAM_ITEMS = [
  { icon: School, title: 'Primary Schools', path: '/choose-path' },
  { icon: GraduationCap, title: 'High Schools', path: '/choose-path' },
  { icon: Users, title: 'Teacher PL', path: '/choose-path' },
  { icon: Building2, title: 'Organisations', path: '/choose-path' },
  { icon: Briefcase, title: 'Workplaces', path: '/choose-path' },
  { icon: Handshake, title: 'Partnerships', path: '/choose-path' },
];

const Browse = () => {
  const navigate = useNavigate();
  const { trackPageView } = useJourney();

  React.useEffect(() => {
    trackPageView('browse');
  }, [trackPageView]);

  return (
    <div 
      data-testid="browse-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-40 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Navigation */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to start
        </Button>

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <img 
            src={WHITE_LOGO}
            alt="Let's Siva"
            className="h-16 mx-auto mb-6 object-contain"
          />
          <h1 
            data-testid="browse-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
          >
            Browse Let's Siva
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-xl mx-auto">
            Explore everything we offer at your own pace.
          </p>
        </div>

        {/* Programs section */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="text-xl font-heading font-bold text-white mb-4">Programs</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {PROGRAM_ITEMS.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.title}
                  data-testid={`browse-${item.title.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => navigate(item.path)}
                  className="bg-stone-900/50 border border-stone-800 p-6 cursor-pointer hover:border-primary/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] rounded-2xl"
                >
                  <Icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-bold text-white">{item.title}</h3>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Resources and Merch */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LS+ */}
          <Card 
            data-testid="browse-resources"
            className="bg-stone-900/50 border border-stone-800 p-8 cursor-pointer hover:border-primary/30 transition-all duration-300 rounded-2xl animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
            onClick={() => navigate('/ls-plus')}
          >
            <BookOpen className="w-12 h-12 text-accent mb-4" />
            <h2 className="text-xl font-heading font-bold text-white mb-2">
              LS+ Resources
            </h2>
            <p className="text-stone-400 mb-4">
              Digital resources to extend the impact beyond the session.
            </p>
            <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80">
              Browse resources
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>

          {/* Merch */}
          <Card 
            data-testid="browse-merch"
            className="bg-stone-900/50 border border-stone-800 p-8 cursor-pointer hover:border-primary/30 transition-all duration-300 rounded-2xl animate-fade-in-up"
            style={{ animationDelay: '0.3s' }}
            onClick={() => navigate('/merch')}
          >
            <ShoppingBag className="w-12 h-12 text-ochre-400 mb-4" />
            <h2 className="text-xl font-heading font-bold text-white mb-2">
              Merch
            </h2>
            <p className="text-stone-400 mb-4">
              Shop, school campaigns, and sponsor a school.
            </p>
            <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80">
              View merch
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        </div>

        {/* Guided journey CTA */}
        <div className="mt-12 text-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <p className="text-stone-500 mb-4">
            Not sure where to start?
          </p>
          <Button
            data-testid="start-guided-journey-btn"
            onClick={() => navigate('/')}
            variant="outline"
            className="rounded-full px-8 py-6 border-primary/50 text-stone-300 hover:bg-primary/10"
          >
            Take the guided journey
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Browse;
